import { BaseWidget } from './BaseWidget.js';

export class SurveyWidget extends BaseWidget {
	constructor(options) {
		super({ ...options, type: 'survey' });

		this.surveyOptions = {
			surveyId: options.surveyId || null,
			surveyType: options.surveyType || 'nps',
			position: options.position || 'center',
			theme: options.theme || 'light',
			enabled:
				typeof options.enabled === 'boolean' ? options.enabled : undefined,
			title: options.title || null,
			description: options.description || null,
			lowLabel: options.lowLabel || null,
			highLabel: options.highLabel || null,
			ratingScale: options.ratingScale || options.scale || null,
			showFeedbackInput:
				typeof options.showFeedbackInput === 'boolean'
					? options.showFeedbackInput
					: null,
			showSubmitButton:
				typeof options.showSubmitButton === 'boolean'
					? options.showSubmitButton
					: null,
			autoSubmitOnSelect:
				typeof options.autoSubmitOnSelect === 'boolean'
					? options.autoSubmitOnSelect
					: null,
			showTitle:
				typeof options.showTitle === 'boolean' ? options.showTitle : null,
			showDescription:
				typeof options.showDescription === 'boolean'
					? options.showDescription
					: null,
			customQuestions: options.customQuestions || [],
			pages: Array.isArray(options.pages) ? options.pages : [],
			thankYouConfig:
				options.thankYouConfig || options.thank_you_config || null,
			respondentId: options.respondentId || null,
			email: options.email || null,
			onSubmit: options.onSubmit || null,
			onDismiss: options.onDismiss || null,
		};

		this.surveyState = {
			score: null,
			feedback: '',
			customAnswers: {},
			pageAnswers: {},
			currentPageIndex: 0,
			isSubmitting: false,
			isVisible: false,
		};
	}

	static removeDanglingElements() {
		if (typeof document === 'undefined') return;

		document
			.querySelectorAll(
				'.feedback-survey, .feedback-survey-backdrop, .feedback-survey-success, .product7-notification'
			)
			.forEach((el) => {
				if (el && el.parentNode) {
					el.parentNode.removeChild(el);
				}
			});
	}

	_render() {
		const container = document.createElement('div');
		container.className = 'feedback-survey-container';
		return container;
	}

	_attachEvents() {
		// Events are attached when survey is shown
	}

	show() {
		if (
			this.options.enabled === false ||
			this.surveyOptions.enabled === false
		) {
			this.sdk.eventBus.emit('survey:suppressed', {
				widget: this,
				surveyId: this.surveyOptions.surveyId,
				reason: 'disabled',
			});
			return this;
		}

		SurveyWidget.removeDanglingElements();
		this._renderSurvey();
		this.state.isOpen = true;
		this.surveyState.isVisible = true;
		this.sdk.eventBus.emit('survey:shown', {
			widget: this,
			type: this.surveyOptions.surveyType,
		});
		return this;
	}

	hide() {
		this.state.isOpen = false;
		this._closeSurvey();
		return this;
	}

	_renderSurvey() {
		this._closeSurvey(false);

		const config = this._getSurveyConfig();
		if (!config) return;
		const isMultiPage = this._isMultiPageSurvey();
		const isLastPage = this._isLastPage();
		const showFeedbackInput = this._shouldShowFeedbackInput();
		const showActions = this._shouldShowActions();
		const showTitle = this._shouldShowTitle(config);
		const showDescription = this._shouldShowDescription(config);
		const progressPercent = isMultiPage
			? Math.round(
					((this.surveyState.currentPageIndex + 1) /
						this.surveyOptions.pages.length) *
						100
				)
			: 0;
		const submitLabel = isMultiPage && !isLastPage ? 'Next' : 'Submit';
		const iconArrowRight = `<iconify-icon icon="ph:arrow-right" width="16" height="16"></iconify-icon>`;
		const iconCheck = `<iconify-icon icon="ph:arrow-right" width="16" height="16"></iconify-icon>`;
		const iconArrowLeft = `<iconify-icon icon="ph:arrow-left" width="16" height="16"></iconify-icon>`;
		const backButton =
			isMultiPage && this.surveyState.currentPageIndex > 0
				? `<button class="feedback-survey-back">${iconArrowLeft} Back</button>`
				: '';

		if (this.surveyOptions.position === 'center') {
			this.backdropElement = document.createElement('div');
			this.backdropElement.className = `feedback-survey-backdrop feedback-survey-backdrop-${this.surveyOptions.theme}`;
			document.body.appendChild(this.backdropElement);
			this.backdropElement.addEventListener('click', () =>
				this._handleDismiss()
			);
		}

		this.surveyElement = document.createElement('div');
		this.surveyElement.className = `feedback-survey feedback-survey-${this.surveyOptions.position} feedback-survey-theme-${this.surveyOptions.theme}${
			showDescription && !showTitle
				? ' feedback-survey-description-primary'
				: ''
		}`;

		this.surveyElement.innerHTML = `
			<button class="feedback-survey-close"><iconify-icon icon="ph:x" width="16" height="16"></iconify-icon></button>
			${showTitle ? `<h3 class="feedback-survey-title">${config.title}</h3>` : ''}
			${showDescription ? `<p class="feedback-survey-description">${config.description}</p>` : ''}
			${isMultiPage && this.surveyOptions.pages.length > 1 ? `<div class="feedback-survey-progress"><div class="feedback-survey-progress-track"><div class="feedback-survey-progress-fill" style="width:${progressPercent}%"></div></div></div>` : ''}
			<div class="feedback-survey-content">${config.html}</div>
			${
				showFeedbackInput
					? `<div class="feedback-survey-feedback">
				<textarea class="feedback-survey-textarea" placeholder="Any additional feedback? (optional)">${this.surveyState.feedback || ''}</textarea>
			</div>`
					: ''
			}
			${
				showActions
					? `<div class="feedback-survey-actions">
				${backButton}
				<button class="feedback-survey-submit">${submitLabel} ${isLastPage ? iconCheck : iconArrowRight}</button>
			</div>`
					: ''
			}
		`;

		document.body.appendChild(this.surveyElement);
		this._attachSurveyEvents();

		requestAnimationFrame(() => {
			if (!this.surveyElement) return;
			this.surveyElement.style.opacity = '1';
			this.surveyElement.style.transform =
				this.surveyOptions.position === 'center'
					? 'translate(-50%, -50%) scale(1)'
					: 'translateY(0)';
		});
	}

	_getSurveyConfig() {
		if (this._isMultiPageSurvey()) {
			return this._getCurrentPageConfig();
		}

		const npsScale = this._getNPSScale();
		const npsUsesSegmentedScale = npsScale.values.length <= 7;
		const npsContainerClass = npsUsesSegmentedScale
			? 'feedback-survey-ces feedback-survey-rating-scale'
			: 'feedback-survey-nps';
		const npsButtonClass = npsUsesSegmentedScale
			? 'feedback-survey-nps-btn feedback-survey-ces-btn feedback-survey-rating-scale-btn'
			: 'feedback-survey-nps-btn';
		const npsLowLabel =
			this.surveyOptions.lowLabel ||
			(npsScale.start === 0 ? 'Not likely' : 'Strongly Disagree');
		const npsHighLabel =
			this.surveyOptions.highLabel ||
			(npsScale.start === 0 ? 'Very likely' : 'Strongly Agree');
		const npsPrompt =
			this.surveyOptions.description ||
			this.surveyOptions.title ||
			'How likely are you to recommend us?';
		const csatPrompt =
			this.surveyOptions.description ||
			this.surveyOptions.title ||
			'How satisfied are you?';
		const cesPrompt =
			this.surveyOptions.description ||
			this.surveyOptions.title ||
			'How easy was it?';

		const configs = {
			nps: {
				title: this.surveyOptions.title || '',
				description: npsPrompt,
				html: `
					<div class="${npsContainerClass}">
						${npsScale.values
							.map(
								(n) => `
							<button class="${npsButtonClass}" data-score="${n}">${n}</button>
						`
							)
							.join('')}
					</div>
					${this._renderScaleLabels(npsLowLabel, npsHighLabel)}
				`,
			},
			csat: {
				title: this.surveyOptions.title || '',
				description: csatPrompt,
				html: `
					<div class="feedback-survey-csat">
						${[
							'\uD83D\uDE1E',
							'\uD83D\uDE15',
							'\uD83D\uDE10',
							'\uD83D\uDE42',
							'\uD83D\uDE04',
						]
							.map(
								(emoji, i) => `
							<button class="feedback-survey-csat-btn" data-score="${i + 1}">${emoji}</button>
						`
							)
							.join('')}
					</div>
					${this._renderScaleLabels(
						this.surveyOptions.lowLabel || 'Very dissatisfied',
						this.surveyOptions.highLabel || 'Very satisfied'
					)}
				`,
			},
			ces: {
				title: this.surveyOptions.title || '',
				description: cesPrompt,
				html: `
					<div class="feedback-survey-ces-list">
						${['Very Difficult', 'Difficult', 'Neutral', 'Easy', 'Very Easy']
							.map(
								(label, i) => `
							<button class="feedback-survey-ces-btn feedback-survey-ces-list-btn" data-score="${i + 1}">${label}</button>
						`
							)
							.join('')}
					</div>
				`,
			},
			custom: {
				title: this.surveyOptions.title || 'Quick Feedback',
				description:
					this.surveyOptions.description ||
					'Help us improve by answering a few questions.',
				html: this._renderCustomQuestions(),
			},
		};

		return configs[this.surveyOptions.surveyType] || null;
	}

	_getNPSScale() {
		const rawScale = Number(this.surveyOptions.ratingScale);
		const scale = Number.isFinite(rawScale) && rawScale >= 2 ? rawScale : 5;
		const start = scale === 11 ? 0 : 1;
		return {
			scale,
			start,
			values: Array.from({ length: scale }, (_, index) => start + index),
		};
	}

	_renderScaleLabels(lowLabel, highLabel) {
		const low = lowLabel || '';
		const high = highLabel || '';
		if (!low && !high) {
			return '';
		}
		return `
			<div class="feedback-survey-labels">
				<span>${low}</span>
				<span>${high}</span>
			</div>
		`;
	}

	_isRatingSurveyType(type = this.surveyOptions.surveyType) {
		return type === 'nps' || type === 'csat' || type === 'ces';
	}

	_shouldShowTitle(config) {
		if (!config || !config.title) {
			return false;
		}
		if (typeof this.surveyOptions.showTitle === 'boolean') {
			return this.surveyOptions.showTitle;
		}
		if (!this._isMultiPageSurvey() && this._isRatingSurveyType()) {
			return !config.description;
		}
		return true;
	}

	_shouldShowDescription(config) {
		if (typeof this.surveyOptions.showDescription === 'boolean') {
			return this.surveyOptions.showDescription;
		}
		if (!this._isMultiPageSurvey() && this._isRatingSurveyType()) {
			return Boolean(config && (config.description || config.title));
		}
		return Boolean(config && config.description);
	}

	_shouldShowFeedbackInput() {
		if (this._isMultiPageSurvey()) {
			return false;
		}
		if (typeof this.surveyOptions.showFeedbackInput === 'boolean') {
			return this.surveyOptions.showFeedbackInput;
		}
		return false;
	}

	_shouldAutoSubmitOnSelect() {
		if (typeof this.surveyOptions.autoSubmitOnSelect === 'boolean') {
			return this.surveyOptions.autoSubmitOnSelect;
		}
		if (this._isMultiPageSurvey()) {
			return false;
		}
		if (this.surveyOptions.showSubmitButton === true) {
			return false;
		}
		return this._isRatingSurveyType() && !this._shouldShowFeedbackInput();
	}

	_shouldShowActions() {
		if (this._isMultiPageSurvey()) {
			return true;
		}
		if (typeof this.surveyOptions.showSubmitButton === 'boolean') {
			return this.surveyOptions.showSubmitButton;
		}
		return !this._shouldAutoSubmitOnSelect();
	}

	_isMultiPageSurvey() {
		return (
			Array.isArray(this.surveyOptions.pages) &&
			this.surveyOptions.pages.length > 0
		);
	}

	_getCurrentPage() {
		if (!this._isMultiPageSurvey()) return null;
		return this.surveyOptions.pages[this.surveyState.currentPageIndex] || null;
	}

	_isLastPage() {
		if (!this._isMultiPageSurvey()) return true;
		return (
			this.surveyState.currentPageIndex >= this.surveyOptions.pages.length - 1
		);
	}

	_getCurrentPageConfig() {
		const page = this._getCurrentPage();
		if (!page) {
			return null;
		}

		return {
			title: page.title || '',
			description: page.description || '',
			html: this._renderSurveyPage(page),
		};
	}

	_renderSurveyPage(page) {
		switch (page.type) {
			case 'rating':
				return this._renderRatingPage(page);
			case 'multiple_choice':
				return this._renderMultipleChoicePage(page);
			case 'text':
				return this._renderTextPage(page);
			default:
				return '';
		}
	}

	_renderRatingPage(page) {
		const pageId = page.id || `page_${this.surveyState.currentPageIndex}`;
		const config = page.rating_config || page.ratingConfig || {};
		const scale = Number(config.scale) || 5;
		const ratingType = config.survey_type || 'number';
		const pageAnswer = this.surveyState.pageAnswers[pageId] || {};
		const currentRating = pageAnswer.rating;

		const labelDefaults = {
			nps:
				scale === 11
					? ['Not likely', 'Very likely']
					: ['Strongly Disagree', 'Strongly Agree'],
			emoji: ['Very dissatisfied', 'Very satisfied'],
			number: ['Poor', 'Excellent'],
			star: ['Poor', 'Excellent'],
		};
		const [defaultLow, defaultHigh] = labelDefaults[ratingType] || ['', ''];
		const lowLabel = config.low_label || defaultLow;
		const highLabel = config.high_label || defaultHigh;
		const labels = this._renderScaleLabels(lowLabel, highLabel);

		if (ratingType === 'nps') {
			const npsScale = Number.isFinite(scale) && scale >= 2 ? scale : 11;
			const start = npsScale === 11 ? 0 : 1;
			const values = Array.from({ length: npsScale }, (_, i) => start + i);
			return `
				<div class="feedback-survey-nps" data-page-id="${pageId}">
					${values
						.map((n) => {
							const selected = currentRating === n ? ' selected' : '';
							return `<button class="feedback-survey-page-rating-btn feedback-survey-nps-btn feedback-survey-nps-score-${n}${selected}" data-page-id="${pageId}" data-score="${n}">${n}</button>`;
						})
						.join('')}
				</div>
				${labels}
			`;
		}

		if (ratingType === 'emoji') {
			const emojis = [
				'\uD83D\uDE1E',
				'\uD83D\uDE15',
				'\uD83D\uDE10',
				'\uD83D\uDE42',
				'\uD83D\uDE04',
			];
			const emojiScale =
				Number.isFinite(scale) && scale >= 2 ? Math.min(scale, 5) : 5;
			return `
				<div class="feedback-survey-csat" data-page-id="${pageId}">
					${emojis
						.slice(0, emojiScale)
						.map((emoji, i) => {
							const score = i + 1;
							const selected = currentRating === score ? ' selected' : '';
							return `<button class="feedback-survey-page-rating-btn feedback-survey-csat-btn${selected}" data-page-id="${pageId}" data-score="${score}">${emoji}</button>`;
						})
						.join('')}
				</div>
				${labels}
			`;
		}

		if (ratingType === 'star') {
			const starScale = Number.isFinite(scale) && scale >= 2 ? scale : 5;
			return `
				<div class="feedback-survey-stars" data-page-id="${pageId}">
					${Array.from({ length: starScale }, (_, i) => {
						const score = i + 1;
						const filled =
							typeof currentRating === 'number' && currentRating >= score
								? ' filled'
								: '';
						return `<button class="feedback-survey-page-rating-btn feedback-survey-star-btn${filled}" data-page-id="${pageId}" data-score="${score}"><iconify-icon icon="ph:star" width="32" height="32" style="pointer-events:none;"></iconify-icon></button>`;
					}).join('')}
				</div>
				${labels}
			`;
		}

		const numScale = Number.isFinite(scale) && scale >= 2 ? scale : 5;
		return `
			<div class="feedback-survey-rating-scale" data-page-id="${pageId}">
				${Array.from({ length: numScale }, (_, i) => {
					const score = i + 1;
					const selected = currentRating === score ? ' selected' : '';
					return `<button class="feedback-survey-page-rating-btn feedback-survey-rating-scale-btn${selected}" data-page-id="${pageId}" data-score="${score}">${score}</button>`;
				}).join('')}
			</div>
			${labels}
		`;
	}

	_renderMultipleChoicePage(page) {
		const pageId = page.id || `page_${this.surveyState.currentPageIndex}`;
		const config =
			page.multipleChoiceConfig || page.multiple_choice_config || {};
		const options = Array.isArray(config.options) ? config.options : [];
		const allowMultiple =
			config.allow_multiple === true ||
			config.multiple === true ||
			config.allow_multiple_selection === true;
		const pageAnswer = this.surveyState.pageAnswers[pageId] || {};
		const selectedValues = Array.isArray(pageAnswer.values)
			? pageAnswer.values
			: pageAnswer.value
				? [pageAnswer.value]
				: [];

		return `
			<div class="feedback-survey-multiple-choice" data-page-id="${pageId}" data-multiple="${allowMultiple}">
				${options
					.map((option, index) => {
						const value =
							option.value || option.id || option.key || `option_${index}`;
						const label = option.label || option.text || String(value);
						const selected = selectedValues.includes(value) ? ' selected' : '';
						return `<button class="feedback-survey-page-choice-btn${selected}" data-page-id="${pageId}" data-value="${value}">${label}</button>`;
					})
					.join('')}
			</div>
		`;
	}

	_renderTextPage(page) {
		const pageId = page.id || `page_${this.surveyState.currentPageIndex}`;
		const pageAnswer = this.surveyState.pageAnswers[pageId] || {};
		const value = pageAnswer.text || '';
		const placeholder = page.placeholder || 'Type your answer...';

		return `
			<div class="feedback-survey-text-page" data-page-id="${pageId}">
				<textarea class="feedback-survey-page-textarea" data-page-id="${pageId}" placeholder="${placeholder}">${value}</textarea>
			</div>
		`;
	}

	_renderCustomQuestions() {
		if (
			!this.surveyOptions.customQuestions ||
			this.surveyOptions.customQuestions.length === 0
		) {
			return `
				<div class="sdk-form-group">
					<label class="sdk-label">What feature do you use most?</label>
					<select class="feedback-survey-select" data-question="feature">
						<option value="">Select a feature</option>
						<option value="feedback">Feedback Collection</option>
						<option value="surveys">Surveys</option>
						<option value="analytics">Analytics</option>
						<option value="integrations">Integrations</option>
					</select>
				</div>
				<div class="sdk-form-group">
					<label class="sdk-label">How often do you use it?</label>
					<div class="feedback-survey-frequency">
						${['Daily', 'Weekly', 'Monthly', 'Rarely']
							.map(
								(freq) => `
							<button class="feedback-survey-freq-btn" data-freq="${freq}">${freq}</button>
						`
							)
							.join('')}
					</div>
				</div>
			`;
		}

		return this.surveyOptions.customQuestions
			.map(
				(q, index) => `
			<div class="sdk-form-group">
				<label class="sdk-label">${q.label}</label>
				${this._renderQuestionInput(q, index)}
			</div>
		`
			)
			.join('');
	}

	_renderQuestionInput(question, index) {
		switch (question.type) {
			case 'select':
				return `
					<select class="feedback-survey-select" data-question="${question.id || index}">
						<option value="">${question.placeholder || 'Select an option'}</option>
						${question.options.map((opt) => `<option value="${opt.value}">${opt.label}</option>`).join('')}
					</select>
				`;
			case 'text':
				return `
					<input type="text" class="feedback-survey-input" data-question="${question.id || index}" placeholder="${question.placeholder || ''}">
				`;
			default:
				return '';
		}
	}

	_attachSurveyEvents() {
		if (!this.surveyElement) return;

		const closeBtn = this.surveyElement.querySelector('.feedback-survey-close');
		closeBtn.addEventListener('click', () => this._handleDismiss());

		const submitBtn = this.surveyElement.querySelector(
			'.feedback-survey-submit'
		);
		if (submitBtn) {
			submitBtn.addEventListener('click', () => this._handleSubmit());
		}

		const backBtn = this.surveyElement.querySelector('.feedback-survey-back');
		if (backBtn) {
			backBtn.addEventListener('click', () => this._handleBack());
		}

		const textarea = this.surveyElement.querySelector(
			'.feedback-survey-textarea'
		);
		if (textarea) {
			textarea.addEventListener('input', (e) => {
				this.surveyState.feedback = e.target.value;
			});
		}

		this._attachTypeSpecificEvents();

		this._escapeHandler = (e) => {
			if (e.key === 'Escape') {
				this._handleDismiss();
			}
		};
		document.addEventListener('keydown', this._escapeHandler);
	}

	_attachTypeSpecificEvents() {
		if (this._isMultiPageSurvey()) {
			this._attachCurrentPageEvents();
			return;
		}

		const type = this.surveyOptions.surveyType;

		if (type === 'nps') {
			this.surveyElement
				.querySelectorAll('.feedback-survey-nps-btn')
				.forEach((btn) => {
					btn.addEventListener('click', () =>
						this._selectNPS(parseInt(btn.dataset.score))
					);
				});
		}

		if (type === 'csat') {
			this.surveyElement
				.querySelectorAll('.feedback-survey-csat-btn')
				.forEach((btn) => {
					btn.addEventListener('click', () =>
						this._selectCSAT(parseInt(btn.dataset.score))
					);
				});
		}

		if (type === 'ces') {
			this.surveyElement
				.querySelectorAll('.feedback-survey-ces-btn')
				.forEach((btn) => {
					btn.addEventListener('click', () =>
						this._selectCES(parseInt(btn.dataset.score))
					);
				});
		}

		if (type === 'custom') {
			this.surveyElement
				.querySelectorAll('.feedback-survey-freq-btn')
				.forEach((btn) => {
					btn.addEventListener('click', () =>
						this._selectFrequency(btn.dataset.freq)
					);
				});

			this.surveyElement
				.querySelectorAll('.feedback-survey-select')
				.forEach((select) => {
					select.addEventListener('change', (e) => {
						this.surveyState.customAnswers[select.dataset.question] =
							e.target.value;
					});
				});

			this.surveyElement
				.querySelectorAll('.feedback-survey-input')
				.forEach((input) => {
					input.addEventListener('input', (e) => {
						this.surveyState.customAnswers[input.dataset.question] =
							e.target.value;
					});
				});
		}
	}

	_attachCurrentPageEvents() {
		const page = this._getCurrentPage();
		if (!page || !this.surveyElement) return;
		const pageId = page.id || `page_${this.surveyState.currentPageIndex}`;

		if (page.type === 'rating') {
			const isStarRating = !!this.surveyElement.querySelector(
				'.feedback-survey-star-btn'
			);

			this.surveyElement
				.querySelectorAll('.feedback-survey-page-rating-btn')
				.forEach((btn) => {
					btn.addEventListener('click', () => {
						const score = parseInt(btn.dataset.score);
						if (Number.isNaN(score)) return;
						this._setPageAnswer(pageId, { rating: score });

						if (isStarRating) {
							this.surveyElement
								.querySelectorAll('.feedback-survey-star-btn')
								.forEach((star) => {
									const starScore = parseInt(star.dataset.score);
									star.classList.toggle('filled', starScore <= score);
								});
						} else {
							this.surveyElement
								.querySelectorAll('.feedback-survey-page-rating-btn')
								.forEach((item) => item.classList.remove('selected'));
							btn.classList.add('selected');
						}
					});

					if (isStarRating) {
						btn.addEventListener('mouseenter', () => {
							const hoverScore = parseInt(btn.dataset.score);
							this.surveyElement
								.querySelectorAll('.feedback-survey-star-btn')
								.forEach((star) => {
									const starScore = parseInt(star.dataset.score);
									star.classList.toggle('hovered', starScore <= hoverScore);
								});
						});
					}
				});

			if (isStarRating) {
				const container = this.surveyElement.querySelector(
					'.feedback-survey-stars'
				);
				if (container) {
					container.addEventListener('mouseleave', () => {
						this.surveyElement
							.querySelectorAll('.feedback-survey-star-btn')
							.forEach((star) => star.classList.remove('hovered'));
					});
				}
			}
		}

		if (page.type === 'multiple_choice') {
			const container = this.surveyElement.querySelector(
				'.feedback-survey-multiple-choice'
			);
			const allowMultiple = container
				? container.dataset.multiple === 'true'
				: false;

			this.surveyElement
				.querySelectorAll('.feedback-survey-page-choice-btn')
				.forEach((btn) => {
					btn.addEventListener('click', () => {
						const value = btn.dataset.value;
						if (!value) return;

						const existing = this.surveyState.pageAnswers[pageId] || {};
						const existingValues = Array.isArray(existing.values)
							? existing.values
							: existing.value
								? [existing.value]
								: [];

						let nextValues = [];
						if (allowMultiple) {
							const hasValue = existingValues.includes(value);
							nextValues = hasValue
								? existingValues.filter((v) => v !== value)
								: [...existingValues, value];
						} else {
							nextValues = [value];
						}

						this._setPageAnswer(pageId, {
							value: nextValues[0] || null,
							values: nextValues,
						});

						if (!allowMultiple) {
							this.surveyElement
								.querySelectorAll('.feedback-survey-page-choice-btn')
								.forEach((item) => item.classList.remove('selected'));
						}
						btn.classList.toggle('selected', nextValues.includes(value));
					});
				});
		}

		if (page.type === 'text') {
			const textarea = this.surveyElement.querySelector(
				'.feedback-survey-page-textarea'
			);
			if (textarea) {
				textarea.addEventListener('input', (e) => {
					this._setPageAnswer(pageId, { text: e.target.value });
				});
			}
		}
	}

	_selectNPS(score) {
		this.surveyState.score = score;
		this.surveyElement
			.querySelectorAll('.feedback-survey-nps-btn')
			.forEach((btn) => {
				const btnScore = parseInt(btn.dataset.score);
				btn.classList.toggle('selected', btnScore === score);
			});
		this._maybeAutoSubmit();
	}

	_selectCSAT(score) {
		this.surveyState.score = score;
		this.surveyElement
			.querySelectorAll('.feedback-survey-csat-btn')
			.forEach((btn) => {
				const btnScore = parseInt(btn.dataset.score);
				btn.classList.toggle('selected', btnScore === score);
			});
		this._maybeAutoSubmit();
	}

	_maybeAutoSubmit() {
		if (!this._shouldAutoSubmitOnSelect()) return;
		this._handleSubmit();
	}

	_setSubmitLoading(loading) {
		const btn = this.surveyElement?.querySelector('.feedback-survey-submit');
		if (!btn) return;
		btn.disabled = loading;
		btn.innerHTML = loading
			? `<iconify-icon icon="ph:circle-notch" width="16" height="16" style="animation: spin 0.8s linear infinite;"></iconify-icon> Submitting...`
			: `Submit <iconify-icon icon="ph:arrow-right" width="16" height="16"></iconify-icon>`;
	}

	async _handleSubmit() {
		const type = this.surveyOptions.surveyType;
		if (this.surveyState.isSubmitting) {
			return;
		}

		if (this._isMultiPageSurvey()) {
			if (!this._validateCurrentPage()) {
				return;
			}

			const nextPageIndex = this._getNextPageIndex();
			if (
				nextPageIndex !== -1 &&
				nextPageIndex !== this.surveyState.currentPageIndex
			) {
				this.surveyState.currentPageIndex = nextPageIndex;
				this._renderSurvey();
				return;
			}
		}

		if (
			!this._isMultiPageSurvey() &&
			(type === 'nps' || type === 'csat' || type === 'ces') &&
			this.surveyState.score === null
		) {
			this._showError('Please select a rating');
			return;
		}

		this.surveyState.isSubmitting = true;
		this._setSubmitLoading(true);

		const respondent = this._getRespondentContext();

		const responseData = {
			answers: this._normalizePageAnswersForSubmit(),
			...(this.surveyState.feedback && { feedback: this.surveyState.feedback }),
			...(respondent.respondentId && { respondentId: respondent.respondentId }),
			...(respondent.email && { email: respondent.email }),
		};

		const response = {
			type: type,
			answers: responseData.answers,
			feedback: this.surveyState.feedback,
			timestamp: new Date().toISOString(),
		};

		try {
			const surveyId =
				this.surveyOptions.surveyId || `local_${type}_${Date.now()}`;
			await this.apiService.submitSurveyResponse(surveyId, responseData);

			this.sdk.eventBus.emit('survey:submitted', { widget: this, response });
			this._showThankYouScreen(() => {
				if (this.surveyOptions.onSubmit) {
					this.surveyOptions.onSubmit(response);
				}
			});
		} catch (error) {
			console.error('[SurveyWidget] Failed to submit survey:', error);
			this._showError('Something went wrong. Please try again.');
		} finally {
			this.surveyState.isSubmitting = false;
			this._setSubmitLoading(false);
		}
	}

	_handleBack() {
		if (!this._isMultiPageSurvey()) return;
		if (this.surveyState.currentPageIndex <= 0) return;
		this.surveyState.currentPageIndex -= 1;
		this._renderSurvey();
	}

	_setPageAnswer(pageId, data) {
		if (!pageId) return;
		this.surveyState.pageAnswers[pageId] = {
			...(this.surveyState.pageAnswers[pageId] || {}),
			...data,
		};

		// Emit question answered event
		const page = this._getCurrentPage();
		this.sdk.eventBus.emit('survey:questionAnswered', {
			widget: this,
			pageId,
			pageType: page?.type,
			answer: this.surveyState.pageAnswers[pageId],
		});
	}

	_validateCurrentPage() {
		const page = this._getCurrentPage();
		if (!page || page.required !== true) return true;
		const pageId = page.id || `page_${this.surveyState.currentPageIndex}`;
		const answer = this.surveyState.pageAnswers[pageId] || {};

		if (page.type === 'rating') {
			if (typeof answer.rating !== 'number') {
				this._showError('Please select a rating');
				return false;
			}
		}

		if (page.type === 'multiple_choice') {
			const hasAnswer =
				Boolean(answer.value) ||
				(Array.isArray(answer.values) && answer.values.length > 0);
			if (!hasAnswer) {
				this._showError('Please select an option');
				return false;
			}
		}

		if (page.type === 'text') {
			if (!answer.text || !String(answer.text).trim()) {
				this._showError('Please enter an answer');
				return false;
			}
		}

		return true;
	}

	_getNextPageIndex() {
		if (!this._isMultiPageSurvey()) return -1;

		const page = this._getCurrentPage();
		const total = this.surveyOptions.pages.length;
		const currentIndex = this.surveyState.currentPageIndex;
		const fallbackNext = currentIndex + 1 < total ? currentIndex + 1 : -1;
		const navigation = page ? page.after_this_page || page.afterThisPage : null;
		if (!navigation) return fallbackNext;

		const conditions = Array.isArray(navigation.conditions)
			? navigation.conditions
			: [];
		const pageId = page.id || `page_${currentIndex}`;
		const answer = this.surveyState.pageAnswers[pageId] || {};

		for (const condition of conditions) {
			if (this._evaluateCondition(condition, page, answer)) {
				return this._resolvePageTarget(condition.go_to, total, fallbackNext);
			}
		}

		return this._resolvePageTarget(navigation.default, total, fallbackNext);
	}

	_evaluateCondition(condition, page, answer) {
		const { operator, values } = condition;
		if (!operator || !Array.isArray(values)) return false;

		let answerValue = null;
		if (page.type === 'rating') {
			answerValue = typeof answer.rating === 'number' ? answer.rating : null;
		} else if (page.type === 'multiple_choice') {
			answerValue =
				answer.value ||
				(Array.isArray(answer.values) ? answer.values[0] : null);
		} else if (page.type === 'text') {
			answerValue = answer.text || null;
		}

		if (operator === 'is_empty')
			return answerValue === null || answerValue === '';
		if (operator === 'not_empty')
			return answerValue !== null && answerValue !== '';
		if (answerValue === null) return false;

		const [v1, v2] = values;
		switch (operator) {
			case 'equals':
				return String(answerValue) === String(v1);
			case 'greater_than':
				return Number(answerValue) > Number(v1);
			case 'less_than':
				return Number(answerValue) < Number(v1);
			case 'between':
				return (
					Number(answerValue) >= Number(v1) && Number(answerValue) <= Number(v2)
				);
			case 'contains':
				return String(answerValue).includes(String(v1));
			default:
				return false;
		}
	}

	_resolvePageTarget(target, total, fallback) {
		if (!target) return fallback;
		if (target === 'end_survey') return -1;
		if (target === 'next_page' || target === 'next') return fallback;
		if (typeof target === 'number')
			return target >= 0 && target < total ? target : fallback;
		if (typeof target === 'string') {
			const normalizedId = target.replace(/^page:/, '');
			const idx = this.surveyOptions.pages.findIndex(
				(p) => p.id === normalizedId
			);
			return idx >= 0 ? idx : fallback;
		}
		return fallback;
	}

	_getSubmissionRating() {
		if (typeof this.surveyState.score === 'number') {
			return this.surveyState.score;
		}

		if (!this._isMultiPageSurvey()) {
			return null;
		}

		for (const page of this.surveyOptions.pages) {
			const pageId =
				page.id || `page_${this.surveyOptions.pages.indexOf(page)}`;
			const answer = this.surveyState.pageAnswers[pageId];
			if (answer && typeof answer.rating === 'number') {
				return answer.rating;
			}
		}

		return null;
	}

	_normalizePageAnswersForSubmit() {
		// Single-rating surveys (NPS / CSAT / CES / star / emoji) have no pages
		if (!this._isMultiPageSurvey()) {
			if (typeof this.surveyState.score === 'number') {
				return [{ page_id: 'p1', value: this.surveyState.score }];
			}
			return [];
		}

		const output = [];

		for (const page of this.surveyOptions.pages) {
			const pageId =
				page.id || `page_${this.surveyOptions.pages.indexOf(page)}`;
			const answer = this.surveyState.pageAnswers[pageId];
			if (answer == null) continue;

			let value = null;

			if (page.type === 'rating' && typeof answer.rating === 'number') {
				value = answer.rating;
			} else if (page.type === 'multiple_choice') {
				const config =
					page.multipleChoiceConfig || page.multiple_choice_config || {};
				const allowMultiple =
					config.allow_multiple === true ||
					config.multiple === true ||
					config.allow_multiple_selection === true;

				if (
					allowMultiple &&
					Array.isArray(answer.values) &&
					answer.values.length > 0
				) {
					value = answer.values;
				} else if (answer.value != null && answer.value !== '') {
					value = answer.value;
				}
			} else if (
				page.type === 'text' &&
				typeof answer.text === 'string' &&
				answer.text.trim()
			) {
				value = answer.text.trim();
			} else if (page.type === 'link' && typeof answer.clicked === 'boolean') {
				value = answer.clicked;
			}

			if (value !== null) {
				output.push({ page_id: pageId, value });
			}
		}

		return output;
	}

	_getRespondentContext() {
		const sdkMetadata =
			typeof this.sdk.getMetadata === 'function'
				? this.sdk.getMetadata() || {}
				: {};
		const apiMetadata =
			this.apiService && typeof this.apiService.getMetadata === 'function'
				? this.apiService.getMetadata() || {}
				: {};
		const localMetadata = this.options.metadata || {};

		return {
			respondentId:
				this.surveyOptions.respondentId ||
				localMetadata.user_id ||
				sdkMetadata.user_id ||
				apiMetadata.user_id ||
				null,
			email:
				this.surveyOptions.email ||
				localMetadata.email ||
				sdkMetadata.email ||
				apiMetadata.email ||
				null,
		};
	}

	async _handleDismiss() {
		if (this.surveyOptions.surveyId) {
			try {
				await this.apiService.dismissSurvey(this.surveyOptions.surveyId);
			} catch (error) {
				console.error('[SurveyWidget] Failed to dismiss survey:', error);
			}
		}

		if (this.surveyOptions.onDismiss) {
			this.surveyOptions.onDismiss();
		}
		this.sdk.eventBus.emit('survey:dismissed', { widget: this });
		this._closeSurvey();
	}

	_showError(message) {
		const existing = this.surveyElement.querySelector('.feedback-survey-error');
		if (existing) existing.remove();

		const error = document.createElement('div');
		error.className = 'feedback-survey-error';
		error.innerHTML = `<iconify-icon icon="ph:warning" width="15" height="15"></iconify-icon><span>${message}</span>`;

		this.surveyElement.prepend(error);

		setTimeout(() => error.remove(), 3000);
	}

	_showThankYouScreen(onAfterClose) {
		if (!this.surveyElement) return;

		const pages = this.surveyOptions.pages || [];
		const currentPage =
			pages[this.surveyState.currentPageIndex] ||
			pages[pages.length - 1] ||
			null;
		const pageConfig = currentPage?.thankYouConfig || null;
		const surveyConfig = this.surveyOptions.thankYouConfig || null;
		const config =
			(pageConfig?.title ? pageConfig : null) ||
			(surveyConfig?.title ? surveyConfig : null) ||
			{};
		const title = config.title || 'Thanks for your feedback!';
		const buttonText = config.button_text || null;
		const rawUrl = config.button_url || '';
		const buttonUrl = rawUrl
			? /^https?:\/\//i.test(rawUrl)
				? rawUrl
				: `https://${rawUrl}`
			: '#';

		if (this._escapeHandler) {
			document.removeEventListener('keydown', this._escapeHandler);
			this._escapeHandler = null;
		}

		this.surveyElement.innerHTML = `
			<button class="feedback-survey-close"><iconify-icon icon="ph:x" width="16" height="16"></iconify-icon></button>
			<div class="feedback-survey-thankyou">
				<iconify-icon icon="ph:check-circle" width="48" height="48" class="feedback-survey-thankyou-icon"></iconify-icon>
				<h3 class="feedback-survey-thankyou-title">${title}</h3>
				${
					buttonText
						? `
					<a class="feedback-survey-thankyou-btn" href="${buttonUrl}" target="_blank" rel="noopener noreferrer">
						${buttonText}
						<iconify-icon icon="ph:arrow-square-out" width="15" height="15" style="pointer-events:none;"></iconify-icon>
					</a>`
						: ''
				}
			</div>
		`;

		const closeBtn = this.surveyElement.querySelector('.feedback-survey-close');
		if (closeBtn) closeBtn.addEventListener('click', () => this._closeSurvey());

		setTimeout(
			() => {
				if (this.surveyElement) this._closeSurvey();
				if (onAfterClose) onAfterClose();
			},
			buttonText ? 6000 : 3500
		);
	}

	_closeSurvey(resetState = true, immediate = false) {
		this.state.isOpen = false;
		if (this._escapeHandler) {
			document.removeEventListener('keydown', this._escapeHandler);
			this._escapeHandler = null;
		}

		const surveyEl = this.surveyElement;
		const backdropEl = this.backdropElement;

		if (surveyEl) {
			surveyEl.style.opacity = '0';
			const pos = this.surveyOptions.position;
			surveyEl.style.transform =
				pos === 'center'
					? 'translate(-50%, -50%) scale(0.95)'
					: pos === 'bottom'
						? 'translateY(100%)'
						: 'translateY(10px)';
			if (immediate) {
				if (surveyEl.parentNode) {
					surveyEl.parentNode.removeChild(surveyEl);
				}
			} else {
				setTimeout(() => {
					if (surveyEl && surveyEl.parentNode) {
						surveyEl.parentNode.removeChild(surveyEl);
					}
				}, 300);
			}
			this.surveyElement = null;
		}

		if (backdropEl) {
			backdropEl.style.opacity = '0';
			if (immediate) {
				if (backdropEl.parentNode) {
					backdropEl.parentNode.removeChild(backdropEl);
				}
			} else {
				setTimeout(() => {
					if (backdropEl && backdropEl.parentNode) {
						backdropEl.parentNode.removeChild(backdropEl);
					}
				}, 300);
			}
			this.backdropElement = null;
		}

		if (resetState) {
			this.surveyState = {
				score: null,
				feedback: '',
				customAnswers: {},
				pageAnswers: {},
				currentPageIndex: 0,
				isSubmitting: false,
				isVisible: false,
			};
		}

		this.sdk.eventBus.emit('survey:closed', { widget: this });
	}

	open() {
		return this.show();
	}

	close() {
		return this.hide();
	}

	toggle() {
		if (this.surveyState.isVisible) {
			return this.hide();
		}

		return this.show();
	}

	destroy() {
		this._closeSurvey(true, true);
		super.destroy();
	}
}
